import React from 'react';
import PropTypes from 'prop-types';
import apolloClient from '../../apollo/client';
import s3SignedUrlQuery from '../../apollo/queries/s3SignedUrl';
import parseS3ObjectRef from '../../helpers/parseS3ObjectRef';
import handleToastError from '../../helpers/handleToastError';
import { AWS_S3_DOCUMENT_BUCKET } from '../../config';

/**
 * Link to a file in one of the private S3 buckets (documents / resources).
 *
 * The objects are not publicly readable, so instead of linking straight to S3
 * the link asks the API for a short-lived signed URL when clicked and opens it
 * in a new tab. The tab is opened synchronously inside the click handler so
 * popup blockers allow it, then pointed at the signed URL once the API answers.
 *
 * Pass either `bucket` + `objectKey`, or `reference` (an object key, or one of
 * the legacy public URLs that older Kids Cafe applications stored).
 */
const S3ObjectLink = ({
  bucket, objectKey, reference, children, className, style,
}) => {
  const target = reference ? parseS3ObjectRef(reference, bucket) : { bucket, key: objectKey };

  if (!target || !target.key) {
    return <span className={className} style={style}>{children}</span>;
  }

  const handleClick = async (event) => {
    event.preventDefault();

    const tab = window.open('', '_blank');
    if (tab) {
      tab.opener = null;
      tab.document.title = 'Opening document...';
    }

    try {
      const { data } = await apolloClient.query({
        query: s3SignedUrlQuery,
        variables: { bucket: target.bucket, key: target.key },
        fetchPolicy: 'network-only',
      });
      const url = data && data.s3SignedUrl && data.s3SignedUrl.url;

      if (!url) {
        throw new Error('The download link could not be created');
      }

      if (tab) {
        tab.location.replace(url);
      } else {
        window.location.assign(url);
      }
    } catch (error) {
      if (tab) {
        tab.close();
      }
      handleToastError(error);
    }
  };

  return (
    // The real URL is minted on click, so there is nothing meaningful to put in href.
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a href="#" onClick={handleClick} className={className} style={style}>
      {children}
    </a>
  );
};

S3ObjectLink.propTypes = {
  bucket: PropTypes.string,
  objectKey: PropTypes.string,
  reference: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

S3ObjectLink.defaultProps = {
  bucket: AWS_S3_DOCUMENT_BUCKET,
  objectKey: null,
  reference: null,
  children: null,
  className: '',
  style: {},
};

export default S3ObjectLink;
